import React, { useState, useEffect } from 'react';
import { Upload, FileText, CheckCircle, XCircle, Clock, Filter, RefreshCw, AlertCircle, Loader } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';

interface EscrowPaymentRelease {
  _id: string;
  escrowPaymentId: {
    _id: string;
    projectId?: any;
    quoteId?: any;
    amount: number;
    currency: string;
    paymentMethod: 'STRIPE' | 'BANK_TRANSFER';
  };
  accountId: string;
  amount: number;
  currency: string;
  status: 'REQUESTED' | 'COMPLETED' | 'ERROR';
  referenceNumber: string;
  bankTransactionId?: string;
  processedAt?: string;
  processedBy?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  errorMessage?: string;
  createdAt: string;
}

export const EscrowPaymentReleases: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [releases, setReleases] = useState<EscrowPaymentRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'REQUESTED' | 'COMPLETED' | 'ERROR'>('ALL');
  const [importResults, setImportResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchReleases();
  }, [statusFilter]);

  const fetchReleases = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') {
        params.append('status', statusFilter);
      }

      const response = await fetch(`${apiUrl}/api/escrow-payment-releases?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setReleases(data.data || []);
      }
    } catch (error) {
      console.error('Fetch releases error:', error);
      setError('Failed to load payment releases');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validExtensions = ['.ofx', '.qfx', '.xml'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExtension)) {
      setError(t('payment.invalidFileType') || 'Invalid file type. Please upload an OFX or QFX file.');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setImportResults(null);

      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${apiUrl}/api/escrow-payment-releases/import-ofx`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setImportResults(data.data);
        await fetchReleases();
      } else {
        setError(data.message || 'Failed to import OFX file');
      }
    } catch (error) {
      console.error('OFX import error:', error);
      setError('Failed to import OFX file');
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return formatDateUtil(dateString);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-5 h-5 text-success-600" />;
      case 'ERROR':
        return <XCircle className="w-5 h-5 text-error-600" />;
      default:
        return <Clock className="w-5 h-5 text-warning-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 dark:bg-gray-700 dark:border dark:border-green-800/50 dark:text-green-200';
      case 'ERROR':
        return 'bg-red-100 text-red-800 dark:bg-gray-700 dark:border dark:border-red-800/50 dark:text-red-200';
      default:
        return 'bg-warning-100 text-warning-800 dark:bg-gray-700 dark:border dark:border-warning-800/50 dark:text-warning-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('payment.escrowPaymentReleases') || 'Escrow Payment Releases'}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('payment.escrowPaymentReleasesDescription') || 'Manage payment releases and import bank transactions from OFX files'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchReleases}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {t('common.refresh') || 'Refresh'}
          </button>
          <label className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 cursor-pointer flex items-center gap-2">
            <Upload className="w-4 h-4" />
            {uploading ? (t('common.uploading') || 'Uploading...') : (t('payment.uploadOFX') || 'Upload OFX File')}
            <input
              type="file"
              accept=".ofx,.qfx,.xml"
              onChange={handleFileUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Import Results */}
      {importResults && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {t('payment.importResults') || 'Import Results'}
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 rounded-lg">
              <div className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                {importResults.totalTransactions}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t('payment.totalTransactions') || 'Total Transactions'}
              </div>
            </div>
            <div className="text-center p-4 bg-green-50 dark:bg-gray-700 dark:border dark:border-green-800/50 rounded-lg">
              <div className="text-2xl font-bold text-success-600 dark:text-success-400">
                {importResults.matched}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t('payment.matched') || 'Matched'}
              </div>
            </div>
            <div className="text-center p-4 bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 rounded-lg">
              <div className="text-2xl font-bold text-warning-600 dark:text-warning-400">
                {importResults.unmatched}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t('payment.unmatched') || 'Unmatched'}
              </div>
            </div>
            <div className="text-center p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 rounded-lg">
              <div className="text-2xl font-bold text-error-600 dark:text-error-400">
                {importResults.errors}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t('payment.errors') || 'Errors'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Filter className="w-5 h-5 text-gray-400" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="ALL">{t('payment.allStatuses') || 'All Statuses'}</option>
          <option value="REQUESTED">{t('payment.requested') || 'Requested'}</option>
          <option value="COMPLETED">{t('payment.completed') || 'Completed'}</option>
          <option value="ERROR">{t('payment.error') || 'Error'}</option>
        </select>
      </div>

      {/* Releases Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 text-primary-600 animate-spin" />
        </div>
      ) : releases.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {t('payment.noReleasesFound') || 'No payment releases found'}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('payment.referenceNumber') || 'Reference'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('payment.amount') || 'Amount'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('payment.status') || 'Status'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('payment.createdAt') || 'Created'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('payment.processedAt') || 'Processed'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('payment.bankTransactionId') || 'Bank Transaction ID'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {releases.map((release) => (
                  <tr key={release._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-mono text-sm text-gray-900 dark:text-gray-100">
                        {release.referenceNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(release.amount, release.currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(release.status)}`}>
                        {getStatusIcon(release.status)}
                        {release.status}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(release.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {release.processedAt ? formatDate(release.processedAt) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-mono text-sm text-gray-900 dark:text-gray-100">
                        {release.bankTransactionId || '-'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

